import { TypedDocumentNode, gql } from '@apollo/client';
import { ReactNode } from 'react';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, HeadlineM } from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_PLAYERS_SLUG } from '@sorare/core/src/constants/routes';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { anyPositionShortNames } from '@sorare/core/src/lib/players';
import { Link } from '@sorare/core/src/routing/Link';

import { Recommendation } from '../Recommendation';
import { DecisiveActions } from './DecisiveActions';
import { PlayerImg } from './PlayerImg';
import { PlayerRecommendation_anyPlayer } from './__generated__/index.graphql';
import { useFollowPlayerQuery } from './useFollowPlayerQuery';

const Content = styled(Vertical)`
  align-items: center;
  width: 100%;
  height: 100%;
  margin-top: calc(-6 * var(--unit));
`;

type Props = {
  player: PlayerRecommendation_anyPlayer;
  chip?: ReactNode;
  onStatsClick: () => void;
  trackForYouClick: () => void;
};

export const PlayerRecommendation = ({
  player,
  chip,
  trackForYouClick,
  onStatsClick,
}: Props) => {
  const { formatMessage } = useIntlContext();
  const { anyCardWithLivePrimaryOffer: anyCard, sport } = player;
  const { generateSportContextPath } = useSportContext();

  const { isIosApp } = useIsMobileApp();

  const followRefetch = useFollowPlayerQuery(player.slug);
  const positions =
    sport !== Sport.NBA &&
    !!player.anyPositions.length &&
    ` - ${player.anyPositions
      .map(p => formatMessage(anyPositionShortNames[p]))
      .join(' - ')}`;

  return (
    <Recommendation
      player={player}
      card={anyCard}
      subscribable={player}
      chip={chip}
      onStatsClick={onStatsClick}
      followRefetch={followRefetch}
      trackForYouClick={trackForYouClick}
    >
      <Content gap={0}>
        <ConditionalWrapper
          wrap={!isIosApp}
          Wrapper={Link}
          props={{
            onClick: trackForYouClick,
            to: generateSportContextPath(ANY_SPORT_PLAYERS_SLUG, {
              params: {
                slug: player?.slug,
              },
            }),
          }}
        >
          <PlayerImg player={player} />
          <DecisiveActions player={player} />
          <Vertical gap={0.5} className="text-center">
            <BodyM as="h6" bold color="var(--c-neutral-600)">
              {player?.activeClub?.name}
              {positions}
            </BodyM>
            <HeadlineM as="p" color="var(--c-neutral-1000)">
              {player.displayName}
            </HeadlineM>
          </Vertical>
        </ConditionalWrapper>
      </Content>
    </Recommendation>
  );
};

PlayerRecommendation.fragments = {
  anyPlayer: gql`
    fragment PlayerRecommendation_anyPlayer on AnyPlayerInterface {
      slug
      displayName
      anyPositions
      sport
      activeClub {
        slug
        name
      }
      anyCardWithLivePrimaryOffer {
        slug
        ...Recommendation_anyCard
      }
      ...Recommendation_anyPlayer
      ...DecisiveActions_anyPlayer
      ...PlayerImg_anyPlayer
    }
    ${Recommendation.fragments.anyCard}
    ${Recommendation.fragments.anyPlayer}
    ${PlayerImg.fragments.anyPlayer}
    ${DecisiveActions.fragments.anyPlayer}
  ` as TypedDocumentNode<PlayerRecommendation_anyPlayer>,
};
